import { useMemo } from 'react'
import { Select, type SelectOption } from '@/components/ui/Select'
import { useStore } from '@/stores'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import type { IndicationId } from '@/types'

export function IndicationSelect() {
  const selectedAssetId = useStore((state) => state.selectedAssetId)
  const selectedIndicationId = useStore((state) => state.selectedIndicationId)
  const setIndication = useStore((state) => state.setIndication)
  const { indications, status, hasAssets } = useTaxonomyStore((state) => ({
    indications: state.data
      ? state.data.indications.filter((ind) => ind.assetId === selectedAssetId).sort((a, b) => a.order - b.order)
      : [],
    status: state.status,
    hasAssets: state.data ? state.data.assets.length > 0 : false,
  }))

  const options = useMemo<SelectOption<IndicationId>[]>(
    () => indications.map((ind) => ({ id: ind.id, label: ind.label })),
    [indications],
  )

  if (options.length === 0) {
    if (!selectedAssetId) {
      // When there are no assets at all, AssetSelect already shows "No assets
      // available" — don't prompt to pick one that doesn't exist.
      if (!hasAssets) {
        return null
      }
      return <p className="text-xs text-gray-400">Select an asset to view related indications.</p>
    }
    if (status === 'error') {
      return <p className="text-xs text-red-400">Unable to load indications. Check your connection.</p>
    }
    return (
      <p className="text-xs text-gray-400">
        {status === 'loading' ? 'Loading indications…' : 'No indications available for this asset.'}
      </p>
    )
  }

  const selectedIndication = indications.find((ind) => ind.id === selectedIndicationId)
  const triggerLabel = selectedIndication ? selectedIndication.label : 'Indication'

  return (
    <Select<IndicationId>
      value={selectedIndicationId}
      onChange={setIndication}
      options={options}
      triggerLabel={triggerLabel}
      ariaLabel="Select indication"
      className="indication-select"
    />
  )
}
